import React from 'react';

import PropTypes from 'prop-types';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';

import BaseModal from './BaseModal';

/**
 * FormModal Component
 *
 * Reusable modal component for form-based CRUD operations (Create, Read, Update, Delete).
 * Built on top of BaseModal with consistent header, body, and footer structure.
 *
 * Features:
 * - Automatic Edit/Create mode detection
 * - Loading states with customizable text
 * - Form submission handling
 * - Cancel/Submit button footer
 * - Disabled submit button support
 * - Consistent styling via CSS classes
 *
 * Usage:
 * ```jsx
 * <FormModal
 *   show={show}
 *   title="Edit Location"
 *   subtitle="Update location information"
 *   loading={updating}
 *   loadingText="Updating..."
 *   submitText="Update Location"
 *   onHide={handleClose}
 *   onSubmit={handleSubmit}
 * >
 *   <ControlledInput label="Name" name="name" value={formData.name} onChange={handleChange} />
 * </FormModal>
 * ```
 *
 * @param {object} props - Component props
 * @param {boolean} props.show - Show/hide the modal
 * @param {string} props.title - Modal title (required)
 * @param {string} props.subtitle - Optional subtitle below title
 * @param {string} props.size - Modal size ('lg', 'wide', etc.)
 * @param {boolean} props.loading - Show loading state
 * @param {string} props.loadingText - Text to show when loading
 * @param {string} props.submitText - Submit button text
 * @param {string} props.cancelText - Cancel button text
 * @param {boolean} props.submitDisabled - Disable submit button
 * @param {string} props.submitVariant - Submit button Bootstrap variant
 * @param {string} props.cancelVariant - Cancel button Bootstrap variant
 * @param {boolean} props.hideFooter - Hide the footer
 * @param {string} props.className - Additional CSS classes
 * @param {function} props.onHide - Close handler (required)
 * @param {function} props.onSubmit - Form submit handler (required)
 * @param {node} props.children - Form fields content
 * @param {node} props.footerContent - Optional custom footer content
 */
const FormModal = ({
    show,
    title,
    subtitle = '',
    size = 'lg',
    loading = false,
    loadingText = 'Loading...',
    submitText = 'Submit',
    cancelText = 'Cancel',
    submitDisabled = false,
    submitVariant = 'primary',
    cancelVariant = 'outline-primary',
    hideFooter = false,
    className = '',
    onHide,
    onSubmit,
    children,
    footerContent = null,
    ...rest
}) => {
    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(e);
    };

    const isSubmitDisabled = loading || submitDisabled;

    // Combine class names
    const modalClasses = [
        'modal-form',
        className,
    ].filter(Boolean).join(' ');

    return (
        <BaseModal
            centered
            show={show}
            size={size}
            className={modalClasses}
            onHide={onHide}
            {...rest}
        >
            <Modal.Header closeButton className="border-0 pb-1">
                <Modal.Title className="modal-title">
                    <h3>{title}</h3>
                    {subtitle && (
                        <p className="text-muted mb-0 mt-1" style={{ fontSize: '0.875rem', fontWeight: 400 }}>
                            {subtitle}
                        </p>
                    )}
                </Modal.Title>
            </Modal.Header>

            <Form onSubmit={handleSubmit}>
                <Modal.Body className="px-4 py-3">
                    {children}
                </Modal.Body>

                {!hideFooter && (
                    <Modal.Footer className="border-0 pt-0 pb-4">
                        {footerContent || (
                            <div className="d-flex justify-content-between w-100">
                                <Button
                                    variant={cancelVariant}
                                    disabled={loading}
                                    onClick={onHide}
                                >
                                    {cancelText}
                                </Button>
                                <Button
                                    type="submit"
                                    variant={submitVariant}
                                    disabled={isSubmitDisabled}
                                >
                                    {loading ? loadingText : submitText}
                                </Button>
                            </div>
                        )}
                    </Modal.Footer>
                )}
            </Form>
        </BaseModal>
    );
};

FormModal.propTypes = {
    // Required props
    show: PropTypes.bool.isRequired,
    title: PropTypes.string.isRequired,
    onHide: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,

    // Optional content
    subtitle: PropTypes.string,
    footerContent: PropTypes.node,

    // Size
    size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'compact', 'default', 'wide', 'full']),

    // Loading state
    loading: PropTypes.bool,
    loadingText: PropTypes.string,

    // Button text
    submitText: PropTypes.string,
    cancelText: PropTypes.string,

    // Button states
    submitDisabled: PropTypes.bool,

    // Button variants
    submitVariant: PropTypes.string,
    cancelVariant: PropTypes.string,

    // Layout options
    hideFooter: PropTypes.bool,

    // Styling
    className: PropTypes.string,
};

export default FormModal;

